/**
 * The reader — §6, §7.
 *
 * THIS COMPONENT HOLDS NO STATE AND NO EFFECTS OF ITS OWN. It reads the page from the
 * URL, asks `usePageScope` what that page means, and lays the shell out. Everything
 * else lives somewhere with a name:
 *
 *   preferences        → `stores/settings/`      (persisted)
 *   session state      → `stores/reader.ts`      (§7.4, never persisted)
 *   what a control does → `lib/commands.ts`      (the one place)
 *   what the page means → `lib/usePageScope.ts`  (§7.4 derivation)
 *   listeners + visits  → `lib/useReaderShell.ts`
 *
 * §7.4 IS STILL THE LOAD-BEARING RULE: page-scoped state is DERIVED against the page on
 * screen, never cleared by whoever navigated. There are six ways the page can change
 * and resetting selection inside any one of them leaves the other five stale.
 */
import { useCallback, type CSSProperties } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { TOTAL_PAGES, clampPage, juzOfPage, surahNamesOnPage } from '@mushaf/corpus'
import { metaQuery, pageQuery, useMeta, useMushafPage } from '../lib/queries'
import {
  closePanel,
  closeSearch,
  goToPage,
  openSearchHit,
  openStudy,
  resetReveal,
  revealNext,
  tapAyah,
  toggleHifzMode,
  toggleSearch,
} from '../lib/commands'
import { usePageScope } from '../lib/usePageScope'
import { useReaderKeyboard, useRecordVisit } from '../lib/useReaderShell'
import { useSwipePage } from '../lib/useSwipePage'
import { useSettings } from '../stores/settings'
import { usePlayer, selectCurrentKey } from '../stores/player'
import { useReader } from '../stores/reader'
import { useQueueTrimOnAutoAdvanceOff } from '../audio/useQueueGrowth'
import { TopBar } from '../components/reader/TopBar'
import { AudioBar } from '../components/reader/AudioBar'
import { HifzBar } from '../components/reader/HifzBar'
import { MushafPageView, firstAyahNumberOnPage } from '../components/reader/MushafPageView'
import { useMeasure } from '../components/reader/useMeasure'
import { StudyPanel } from '../components/panel/StudyPanel'
import { SearchPanel } from '../components/search/SearchPanel'
import { Button } from '../components/ui'
import { useIsDesktop } from '../lib/useMediaQuery'

export const Route = createFileRoute('/read/$page')({
  // Warm meta and the page JSON in the loader so the first paint has data.
  loader: async ({ context, params }) => {
    const meta = await context.queryClient.ensureQueryData(metaQuery)
    const withV4 = useSettings.getState().mode === 'tajweed'
    // CLAMP RATHER THAN THROW — a bad URL should still render a page (§6).
    void context.queryClient.ensureQueryData(
      pageQuery(clampPage(Number(params.page)), meta, withV4),
    )
    return null
  },
  component: Reader,
})

function Reader() {
  // The URL is the OWNER of "which page is on screen" (§4.1). It is clamped here
  // rather than validated in the route, so a hand-typed /read/9999 still renders.
  const { page: rawPage } = Route.useParams()
  const page = clampPage(Number(rawPage))

  const { data: meta } = useMeta()
  const mode = useSettings((s) => s.mode)
  const fontScale = useSettings((s) => s.fontScale)
  const hifzMode = useSettings((s) => s.hifzMode)
  const hideText = useSettings((s) => s.hideText)
  const quiz = useSettings((s) => s.quiz)

  const { data: loaded } = useMushafPage(page, mode === 'tajweed')
  const playingKey = usePlayer(selectCurrentKey)

  const panel = useReader((s) => s.panel)
  const markingMode = useReader((s) => s.markingMode)

  const scope = usePageScope(page, meta)

  useRecordVisit(page)
  useReaderKeyboard(page)
  useQueueTrimOnAutoAdvanceOff(meta)

  // --- measurement (§7.6) --------------------------------------------------
  const { stageRef, cardRef, bodyRef, fit, cardWidth, justified } = useMeasure({
    pageKey: page,
    mode,
    fontScale,
    v4Ready: loaded?.v4Ready ?? false,
    ready: Boolean(loaded),
  })

  /**
   * Swipe the stage to turn the page — same clamping path as the chevrons and the arrow
   * keys, so 1 and 604 are harmless no-ops rather than disabled edges (T65).
   */
  useSwipePage(
    stageRef,
    useCallback((direction: 1 | -1) => goToPage(clampPage(page + direction)), [page]),
  )

  const surahNames = meta ? surahNamesOnPage(meta, page) : []

  /**
   * The grid reserves the panel column KEYED ON WHAT IS ACTUALLY RENDERED, not on the
   * panel mode: a study selection left behind on the previous page closes the panel,
   * and reserving its column anyway leaves a third of the reader empty.
   */
  const panelContent =
    panel === 'search' ? (
      <SearchPanel onOpenHit={openSearchHit} onClose={closeSearch} />
    ) : panel === 'study' && scope.selectedHere ? (
      <StudyPanel
        meta={meta}
        page={page}
        verseKey={scope.selectedHere}
        ayahKeys={scope.ayahKeys}
        onSelect={openStudy}
        onClose={closePanel}
      />
    ) : null

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TopBar
        meta={meta}
        page={page}
        juz={meta ? juzOfPage(meta, page) : 1}
        surahName={surahNames[0] ?? '—'}
        searchOpen={panel === 'search'}
        hifzMode={hifzMode}
        onToggleSearch={toggleSearch}
        onToggleHifz={toggleHifzMode}
        onNavigate={goToPage}
      />

      <div
        className={`grid min-h-0 flex-1 gap-3 p-3 ${
          panelContent ? 'lg:grid-cols-[1fr_minmax(340px,33%)]' : ''
        }`}
      >
        <div ref={stageRef} className="mushaf-stage">
          <div
            ref={cardRef}
            className="mushaf-card"
            style={{
              // @ts-expect-error CSS custom properties are not in CSSProperties.
              '--fit': fit,
              '--card-width': cardWidth !== null ? `${cardWidth}px` : undefined,
            }}
          >
            <div className="mushaf-head">
              <span>{surahNames.join(' · ')}</span>
              <span className="flex-1" />
              {quiz === 'reveal' && (
                <span className="flex gap-1">
                  <Button
                    tone="soft"
                    className="px-2 py-0.5 text-[11px]"
                    onClick={() => revealNext(scope.revealScope, scope.ayahKeys.length)}
                  >
                    اكشف التالية
                  </Button>
                  <Button
                    tone="ghost"
                    className="px-2 py-0.5 text-[11px]"
                    onClick={() => resetReveal(scope.revealScope)}
                  >
                    إعادة
                  </Button>
                </span>
              )}
              <span>صفحة {page}</span>
            </div>

            <div
              ref={bodyRef}
              className={`mushaf-body ${hideText ? 'is-hidden-text' : ''} ${
                markingMode === 'tap' ? 'is-marking' : ''
              }`}
            >
              {loaded ? (
                <MushafPageView
                  data={loaded.data}
                  mode={mode}
                  v4Ready={loaded.v4Ready}
                  justified={justified}
                  selectedKey={scope.selectedHere}
                  playingKey={playingKey}
                  rangedKeys={scope.rangedKeys}
                  bookmarkedKeys={scope.bookmarkedKeys}
                  hiddenKeys={scope.hiddenKeys}
                  quiz={quiz}
                  onSelectAyah={tapAyah}
                />
              ) : (
                // L1: no page data before its fonts, so there is nothing to paint yet.
                <p className="p-8 text-center text-sm text-[var(--ink-faint)]">جارٍ التحميل…</p>
              )}
            </div>

            <div className="mushaf-foot">
              <span>{page}</span>
            </div>
          </div>
        </div>

        {/**
         * BELOW `lg` THE PANEL IS A BOTTOM SHEET, not a grid row. As a grid row on a
         * phone it sat a full screen-height below the fold, so tapping an ayah looked
         * like it had done nothing at all (T67).
         *
         * Built ONCE and mounted in ONE of two places — never both behind a `hidden`
         * class, because two controls carrying the same accessible name are ambiguous
         * to a screen reader and break getByLabel in tests (§14.5).
         */}
        {panelContent && <PanelSlot>{panelContent}</PanelSlot>}
      </div>

      {hifzMode && (
        <HifzBar
          meta={meta}
          page={page}
          ayahKeys={scope.ayahKeys}
          range={scope.rangeHere}
          drillKeys={scope.drillKeys}
        />
      )}

      <AudioBar meta={meta} page={page} ayahKeys={scope.ayahKeys} />

      <span className="sr-only">
        {loaded ? `أول آية في الصفحة ${firstAyahNumberOnPage(loaded.data)}` : ''}
        {page < TOTAL_PAGES ? '' : ' آخر صفحة'}
      </span>
    </div>
  )
}

/**
 * Desktop: a grid column. Below `lg`: a sheet IN FRONT OF the page.
 *
 * BRANCHED, not two arms behind a `hidden` class — the panel is built once and
 * mounted in one of two places, so there is never a second control with the same
 * accessible name (§14.5, §7.1).
 */
function PanelSlot({ children }: { children: React.ReactNode }) {
  const isDesktop = useIsDesktop()

  if (isDesktop) return <div className="min-h-0">{children}</div>

  /**
   * `dvh`, NOT `vh` (and the inset, not bare `bottom-0`).
   *
   * `vh` resolves against the LARGE viewport — the one with the URL bar hidden — so with
   * the bar showing a `70vh` sheet is taller than the space it has, and the bottom of
   * the panel (which is where its close control and its tab content live) was pushed
   * off-screen with nothing to scroll. `dvh` tracks the bar.
   */
  return (
    <div
      className="safe-b fixed inset-x-0 bottom-0 z-40 max-h-[70dvh] min-h-0 p-2"
      style={{ '--safe-b-base': '0.5rem' } as CSSProperties}
    >
      <div
        className="max-h-[68dvh] overflow-hidden rounded-2xl"
        style={{ boxShadow: 'var(--shadow-popup)' }}
      >
        {children}
      </div>
    </div>
  )
}
